import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { exportToPDF, exportToWord, sendEmail } from '../../utils/exportUtils';

const { FiX, FiFileText, FiDownload, FiMail, FiCheckCircle, FiLoader } = FiIcons;

const ExportModal = ({ isOpen, onClose, moduleData }) => {
  const [isGenerating, setIsGenerating] = useState(null);

  if (!isOpen) return null;

  const completedCount = Object.values(moduleData).filter(m => m.completed).length;

  const handleExport = async (type, fn) => {
    setIsGenerating(type);
    try {
      await fn(moduleData);
      // Small delay to show completion state
      setTimeout(() => setIsGenerating(null), 1000);
    } catch (err) {
      setIsGenerating(null);
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        >
          <div className="p-6 border-b flex items-center justify-between bg-slate-50">
            <h3 className="text-xl font-bold text-slate-800">Export Workbook</h3>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1">
              <SafeIcon icon={FiX} className="text-xl" />
            </button>
          </div>

          <div className="p-8">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-50 text-blue-600 rounded-full mb-4">
                <SafeIcon icon={FiCheckCircle} className="text-3xl" />
              </div>
              <p className="text-slate-600">
                You've completed <span className="font-bold text-blue-600">{completedCount}/5</span> modules.
                Your progress is saved and ready for export.
              </p>
            </div>

            <div className="space-y-4">
              <button
                disabled={isGenerating !== null}
                onClick={() => handleExport('pdf', exportToPDF)}
                className="w-full flex items-center justify-between p-4 bg-white border-2 border-slate-100 hover:border-blue-500 hover:bg-blue-50/50 rounded-xl transition-all group disabled:opacity-50"
              >
                <div className="flex items-center space-x-4">
                  <div className="p-2 bg-red-50 text-red-600 rounded-lg">
                    <SafeIcon icon={isGenerating === 'pdf' ? FiLoader : FiFileText} className={isGenerating === 'pdf' ? 'animate-spin' : ''} />
                  </div>
                  <div className="text-left">
                    <div className="font-bold text-slate-800">Download PDF</div>
                    <div className="text-xs text-slate-500">
                      {isGenerating === 'pdf' ? 'Generating...' : 'Best for printing & reading'}
                    </div>
                  </div>
                </div>
                {isGenerating !== 'pdf' && <SafeIcon icon={FiDownload} className="text-slate-300 group-hover:text-blue-500" />}
              </button>

              <button
                disabled={isGenerating !== null}
                onClick={() => handleExport('word', exportToWord)}
                className="w-full flex items-center justify-between p-4 bg-white border-2 border-slate-100 hover:border-blue-500 hover:bg-blue-50/50 rounded-xl transition-all group disabled:opacity-50"
              >
                <div className="flex items-center space-x-4">
                  <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
                    <SafeIcon icon={isGenerating === 'word' ? FiLoader : FiFileText} className={isGenerating === 'word' ? 'animate-spin' : ''} />
                  </div>
                  <div className="text-left">
                    <div className="font-bold text-slate-800">Download Word (.docx)</div>
                    <div className="text-xs text-slate-500">
                      {isGenerating === 'word' ? 'Generating...' : 'Best for editing in Word'}
                    </div>
                  </div>
                </div>
                {isGenerating !== 'word' && <SafeIcon icon={FiDownload} className="text-slate-300 group-hover:text-blue-500" />}
              </button>

              <button
                onClick={() => sendEmail(moduleData)}
                className="w-full flex items-center justify-between p-4 bg-white border-2 border-slate-100 hover:border-orange-500 hover:bg-orange-50/50 rounded-xl transition-all group"
              >
                <div className="flex items-center space-x-4">
                  <div className="p-2 bg-orange-50 text-orange-600 rounded-lg">
                    <SafeIcon icon={FiMail} />
                  </div>
                  <div className="text-left">
                    <div className="font-bold text-slate-800">Email Summary</div>
                    <div className="text-xs text-slate-500">Send summary to your inbox</div>
                  </div>
                </div>
                <SafeIcon icon={FiMail} className="text-slate-300 group-hover:text-orange-500" />
              </button>
            </div>
          </div>

          <div className="p-4 bg-slate-50 text-center">
            <p className="text-xs text-slate-400">
              GospelWise Author • CROSS + PEN Coaching
            </p>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ExportModal;